// src/Components/Modules/HR/HrChatAI.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Fade,
  Zoom,
  Badge as MuiBadge,
} from "@mui/material";
import {
  FaRobot,
  FaPaperPlane,
  FaUser,
  FaMicrophone,
  FaQuestionCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCalendarCheck,
  FaBriefcase,
  FaSync,
  FaTrash,
  FaInfoCircle,
  FaLightbulb,
  FaChartLine,
  FaUsers,
  FaCog,
  FaStar,
  FaThumbsUp,
  FaThumbsDown,
} from "react-icons/fa";
import { styled, keyframes } from "@mui/material/styles";
import Swal from "sweetalert2";

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(3),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const ChatContainer = styled(Paper)(({ theme }) => ({
  height: "calc(100vh - 320px)",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(3),
  overflow: "hidden",
  boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  border: "1px solid rgba(0,0,0,0.08)",
}));

const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  background: "linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: 10,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#667eea",
    borderRadius: 10,
    "&:hover": {
      background: "#764ba2",
    },
  },
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  display: "flex",
  justifyContent: isUser ? "flex-end" : "flex-start",
  marginBottom: theme.spacing(2),
  animation: `${slideIn} 0.3s ease-out`,
}));

const BubbleContent = styled(Box)(({ theme, isUser }) => ({
  maxWidth: "70%",
  padding: theme.spacing(1.5, 2),
  borderRadius: isUser ? "20px 20px 5px 20px" : "20px 20px 20px 5px",
  background: isUser
    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    : "white",
  color: isUser ? "white" : "#333",
  boxShadow: isUser
    ? "0 4px 15px rgba(102, 126, 234, 0.3)"
    : "0 2px 10px rgba(0,0,0,0.08)",
  border: isUser ? "none" : "1px solid rgba(0,0,0,0.08)",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: isUser
      ? "0 6px 20px rgba(102, 126, 234, 0.4)"
      : "0 4px 15px rgba(0,0,0,0.12)",
  },
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 2),
}));

const TypingDot = styled(Box)(({ theme, delay }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#667eea",
  animation: `${pulse} 1.4s infinite`,
  animationDelay: `${delay}s`,
}));

const InputArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: "white",
  borderTop: "1px solid rgba(0,0,0,0.08)",
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: "none",
  fontWeight: 500,
  padding: theme.spacing(0.5, 2),
  border: "1px solid rgba(102, 126, 234, 0.3)",
  color: "#667eea",
  background: "white",
  "&:hover": {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    borderColor: "transparent",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
}));

const HrChatAI = () => {
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "👋 Hi there! I'm your AI HR Assistant. I can help you with questions about attendance, payroll, leave policies, and more. How can I assist you today?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const endRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const quickActions = useMemo(
    () => [
      { icon: FaCalendarCheck, text: "How to apply for leave?", category: "leave" },
      { icon: FaMoneyBillWave, text: "When is payday?", category: "payroll" },
      { icon: FaCalendarAlt, text: "Check my attendance", category: "attendance" },
      { icon: FaBriefcase, text: "Company policies", category: "policy" },
    ],
    []
  );

  const send = async (text = null) => {
    const messageText = text || input;
    if (!messageText.trim()) return;

    const userMsg = messageText;
    setInput("");
    setLoading(true);
    setMessageCount((prev) => prev + 1);

    setMessages((prev) => [
      ...prev,
      { role: "user", text: userMsg, timestamp: new Date() },
    ]);

    try {
      const res = await AxiosAuthInstance.post("hr/ai/ask", { prompt: userMsg });
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: res.data.reply,
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "⚠️ Sorry, I'm having trouble connecting to the AI service. Please try again in a moment.",
          timestamp: new Date(),
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault();
      send();
    }
  };

  const clearChat = () => {
    Swal.fire({
      title: "Clear Chat History?",
      text: "This will remove all messages from the current session.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Clear",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setMessages([
          {
            role: "bot",
            text: "🤖 Chat cleared! How can I help you today?",
            timestamp: new Date(),
          },
        ]);
        setMessageCount(0);
      }
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="AI HR Assistant" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaRobot style={{ marginRight: 10, verticalAlign: "middle" }} />
              AI HR Assistant
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Get instant answers to your HR questions powered by AI
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Clear Chat">
              <IconButton
                onClick={clearChat}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <FaTrash />
              </IconButton>
            </Tooltip>
            <Tooltip title="Chat Info">
              <IconButton
                onClick={() => {
                  Swal.fire({
                    icon: "info",
                    title: "About AI HR Assistant",
                    html: `
                      <div style="text-align: left; padding: 10px;">
                        <p><strong>Messages:</strong> ${messageCount}</p>
                        <p><strong>Capabilities:</strong></p>
                        <ul style="padding-left: 20px;">
                          <li>Attendance queries</li>
                          <li>Payroll information</li>
                          <li>Leave policies</li>
                          <li>Company guidelines</li>
                        </ul>
                      </div>
                    `,
                    confirmButtonColor: "#667eea",
                  });
                }}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <FaInfoCircle />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </GradientPaper>

      {/* Quick Actions */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FaLightbulb style={{ color: "#ffc107", marginRight: 8 }} />
          <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
            Quick Actions:
          </Typography>
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <QuickActionButton
                key={index}
                size="small"
                startIcon={<IconComponent />}
                onClick={() => send(action.text)}
                disabled={loading}
              >
                {action.text}
              </QuickActionButton>
            );
          })}
        </Stack>
      </Paper>

      {/* Chat Container */}
      <ChatContainer>
        {/* Messages Area */}
        <MessagesArea>
          {messages.map((m, i) => (
            <MessageBubble key={i} isUser={m.role === "user"}>
              {m.role === "bot" && (
                <Avatar
                  sx={{
                    bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    width: 36,
                    height: 36,
                    mt: 0.5,
                  }}
                >
                  <FaRobot />
                </Avatar>
              )}
              <Box>
                <BubbleContent isUser={m.role === "user"}>
                  {m.role === "user" && <FaUser style={{ marginTop: 4, flexShrink: 0 }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                      }}
                    >
                      {m.text}
                    </Typography>
                  </Box>
                </BubbleContent>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    px: 1,
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    textAlign: m.role === "user" ? "right" : "left",
                  }}
                >
                  {formatTime(m.timestamp)}
                </Typography>
              </Box>
              {m.role === "user" && (
                <Avatar
                  sx={{
                    bgcolor: "#4351A3",
                    width: 36,
                    height: 36,
                    mt: 0.5,
                  }}
                >
                  <FaUser />
                </Avatar>
              )}
            </MessageBubble>
          ))}

          {/* Typing Indicator */}
          {loading && (
            <MessageBubble isUser={false}>
              <Avatar
                sx={{
                  bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  width: 36,
                  height: 36,
                }}
              >
                <FaRobot />
              </Avatar>
              <BubbleContent isUser={false}>
                <TypingIndicator>
                  <TypingDot delay={0} />
                  <TypingDot delay={0.2} />
                  <TypingDot delay={0.4} />
                </TypingIndicator>
              </BubbleContent>
            </MessageBubble>
          )}

          <div ref={endRef} />
        </MessagesArea>

        {/* Input Area */}
        <InputArea>
          <TextField
            fullWidth
            placeholder="Ask me anything about HR..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={loading}
            inputRef={inputRef}
            variant="outlined"
            size="small"
            multiline
            maxRows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: "#f8f9ff",
                "&:hover": {
                  bgcolor: "#f0f2ff",
                },
                "&.Mui-focused": {
                  bgcolor: "white",
                },
              },
            }}
          />
          <Tooltip title={loading ? "Sending..." : "Send Message"}>
            <span>
              <IconButton
                onClick={() => send()}
                disabled={loading || !input.trim()}
                sx={{
                  bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  width: 48,
                  height: 48,
                  "&:hover": {
                    background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                  },
                  "&.Mui-disabled": {
                    background: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {loading ? <FaSync className="fa-spin" /> : <FaPaperPlane />}
              </IconButton>
            </span>
          </Tooltip>
        </InputArea>
      </ChatContainer>

      {/* Footer Info */}
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          <FaInfoCircle style={{ verticalAlign: "middle", marginRight: 4 }} />
          AI Assistant is here to help. For complex issues, please contact HR directly.
        </Typography>
      </Box>
    </Box>
  );
};

export default HrChatAI;